/* 🎆 4th July Fireworks — Independence Day Match-3 Board (Mar 2026)
 * ---------------------------------------------------------------------
 * Same core mechanics as JewelsGame (60s runs, cascading combos, box
 * claims) but with a patriotic gem set + red/white/blue theming. Auto-
 * unlocks during the July 1–7 PT window (owner can force-toggle any time).
 */
import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIZE = 8;
// Patriotic gem set — matches backend fireworks_service.FIREWORKS_GEMS
const GEMS = ["⭐", "🚀", "🇺🇸", "🎆", "🦅", "🔔", "✨"];
const RUN_SECONDS = 60;

const idx = (r, c) => r * SIZE + c;
const randGem = () => GEMS[Math.floor(Math.random() * GEMS.length)];

function genBoard() {
  const b = new Array(SIZE * SIZE).fill(null);
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      let g;
      do {
        g = randGem();
      } while (
        (c >= 2 && b[idx(r, c - 1)] === g && b[idx(r, c - 2)] === g) ||
        (r >= 2 && b[idx(r - 1, c)] === g && b[idx(r - 2, c)] === g)
      );
      b[idx(r, c)] = g;
    }
  }
  return b;
}
function findMatches(b) {
  const hit = new Set();
  for (let r = 0; r < SIZE; r++)
    for (let c = 0; c < SIZE - 2; c++) {
      const g = b[idx(r, c)];
      if (g && g === b[idx(r, c + 1)] && g === b[idx(r, c + 2)]) {
        let e = c + 2;
        while (e + 1 < SIZE && b[idx(r, e + 1)] === g) e++;
        for (let k = c; k <= e; k++) hit.add(idx(r, k));
      }
    }
  for (let c = 0; c < SIZE; c++)
    for (let r = 0; r < SIZE - 2; r++) {
      const g = b[idx(r, c)];
      if (g && g === b[idx(r + 1, c)] && g === b[idx(r + 2, c)]) {
        let e = r + 2;
        while (e + 1 < SIZE && b[idx(e + 1, c)] === g) e++;
        for (let k = r; k <= e; k++) hit.add(idx(k, c));
      }
    }
  return hit;
}
function collapse(b) {
  const nb = [...b];
  for (let c = 0; c < SIZE; c++) {
    let write = SIZE - 1;
    for (let r = SIZE - 1; r >= 0; r--) {
      if (nb[idx(r, c)] !== null) {
        nb[idx(write, c)] = nb[idx(r, c)];
        if (write !== r) nb[idx(r, c)] = null;
        write--;
      }
    }
    for (let r = write; r >= 0; r--) nb[idx(r, c)] = randGem();
  }
  return nb;
}

export default function FireworksGame() {
  const { user } = useAuth();
  const [state, setState]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [board, setBoard]     = useState(null);
  const [selected, setSelected] = useState(null);
  const [clearing, setClearing] = useState(new Set());
  const [score, setScore]     = useState(0);
  const [timeLeft, setTimeLeft] = useState(RUN_SECONDS);
  const [running, setRunning] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [claiming, setClaiming] = useState(null);
  const scoreRef  = useRef(0);
  const runRef    = useRef(false);

  const fetchState = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/arcade/fireworks/state`);
      setState(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load Fireworks board.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchState(); }, [fetchState]);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(id); endRun(); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const startRun = () => {
    setBoard(genBoard()); setScore(0); scoreRef.current = 0;
    setTimeLeft(RUN_SECONDS); setSelected(null); setRunning(true); runRef.current = true;
  };
  const endRun = async () => {
    setRunning(false); runRef.current = false; setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/arcade/fireworks/complete`, { score: scoreRef.current });
      toast.success(`🎆 Run banked: +${data.score_added} pts → ${data.total_score.toLocaleString()} total!`);
      fetchState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit run.");
    } finally { setSubmitting(false); }
  };

  const resolveMatches = useCallback((startBoard) => {
    setResolving(true);
    let combo = 0;
    const step = (b) => {
      const matches = findMatches(b);
      if (matches.size === 0) { setClearing(new Set()); setResolving(false); return; }
      combo += 1;
      const gained = matches.size * 12 * combo;   // slightly higher per-match than Jewels
      if (runRef.current) { scoreRef.current += gained; setScore(scoreRef.current); }
      setClearing(new Set(matches));
      setTimeout(() => {
        const nb = [...b];
        matches.forEach(i => { nb[i] = null; });
        const filled = collapse(nb);
        setClearing(new Set());
        setBoard(filled);
        setTimeout(() => step(filled), 180);
      }, 260);
    };
    step(startBoard);
  }, []);

  const onCellClick = (r, c) => {
    if (!running || resolving) return;
    const i = idx(r, c);
    if (selected === null) { setSelected(i); return; }
    if (selected === i)    { setSelected(null); return; }
    const sr = Math.floor(selected / SIZE), sc = selected % SIZE;
    const adjacent = Math.abs(sr - r) + Math.abs(sc - c) === 1;
    if (!adjacent) { setSelected(i); return; }
    const nb = [...board];
    [nb[selected], nb[i]] = [nb[i], nb[selected]];
    setSelected(null);
    if (findMatches(nb).size === 0) {
      setBoard(nb); setTimeout(() => setBoard(board), 200); return;
    }
    setBoard(nb); resolveMatches(nb);
  };

  const claimBox = async (b) => {
    setClaiming(b.index);
    try {
      const { data } = await axios.post(`${API}/arcade/fireworks/claim-box`, { box_index: b.index });
      const spinNote = data.extra_spins ? ` · +${data.extra_spins} 🎰 Extra Spin${data.extra_spins === 1 ? "" : "s"}` : "";
      toast.success(`${b.icon} ${data.box_label} → +${data.reward_pt} Phoenix Tokens${spinNote}!`);
      fetchState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't claim box.");
    } finally { setClaiming(null); }
  };

  const ownerToggle = async (active) => {
    try {
      await axios.post(`${API}/arcade/fireworks/admin/set`,
        { active, label: "🎆 Owner-triggered Fireworks", days: 7 });
      toast.success(active ? "🎆 7-day Fireworks event started!" : "Event ended.");
      fetchState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed.");
    }
  };

  if (loading) {
    return <div className="text-center py-12"><Loader2 className="animate-spin inline text-red-400" /></div>;
  }

  // Locked (outside July 1–7 window)
  if (!state?.active) {
    return (
      <div data-testid="fireworks-locked" className="text-center py-10">
        <div className="text-6xl mb-4">🎆</div>
        <h3 className="font-display text-3xl text-white mb-2">4th July Fireworks</h3>
        <p className="text-neutral-400 text-sm max-w-md mx-auto">
          {state?.message || "The Fireworks board lights up July 1–7. See you then, patriot."}
        </p>
        <div className="mt-6 flex justify-center gap-2 flex-wrap">
          {(state?.boxes || []).map((b) => (
            <div key={b.index}
                 className="w-16 py-1.5 border border-zinc-800 bg-black/40 flex flex-col items-center opacity-50">
              <span className="text-xl">{b.icon}</span>
              <span className="text-[8px] text-neutral-500">{b.threshold.toLocaleString()}</span>
            </div>
          ))}
        </div>
        {user?.role === "owner" && (
          <button data-testid="fireworks-owner-start" onClick={() => ownerToggle(true)}
                  className="mt-6 px-4 py-2 border border-red-500/60 text-red-300 text-xs font-bold uppercase tracking-widest hover:bg-red-500/10">
            👑 Owner: Start 7-Day Fireworks Event
          </button>
        )}
      </div>
    );
  }

  const boxes = state.boxes || [];
  const runsToday = state.runs_today ?? 0;
  const maxRuns = state.max_runs ?? 12;
  return (
    <div data-testid="fireworks-arcade" className="text-center relative">
      {/* Patriotic gradient bar */}
      <div className="h-1 w-full rounded-full mb-3"
           style={{ background: "linear-gradient(90deg,#DC2626 0%,#DC2626 33%,#FFFFFF 33%,#FFFFFF 66%,#3B82F6 66%,#3B82F6 100%)" }} />
      <div className="flex items-center justify-center gap-3 mb-1 flex-wrap">
        <h3 className="font-display text-3xl text-white">🎆 Fireworks</h3>
        <span className="px-3 py-1 border border-red-500/60 text-red-300 text-[10px] font-black uppercase tracking-widest">
          {state.event_label}
        </span>
      </div>
      <p className="text-neutral-400 text-xs mb-4">
        Swap patriots · 3+ clears · cascades multiply · 60s runs · {runsToday}/{maxRuns} today
      </p>

      {/* Reward boxes */}
      <div className="flex justify-center gap-2 mb-5 flex-wrap" data-testid="fireworks-boxes">
        {boxes.map((b) => (
          <button
            key={b.index}
            data-testid={`fireworks-box-${b.index}`}
            disabled={!b.unlocked || b.claimed || claiming === b.index}
            onClick={() => claimBox(b)}
            className={`w-24 py-2 border-2 flex flex-col items-center transition-all ${
              b.claimed
                ? "border-emerald-700 bg-emerald-950/30 opacity-60"
                : b.unlocked
                  ? "border-red-500 bg-red-500/10 animate-pulse cursor-pointer hover:scale-105"
                  : "border-zinc-800 bg-black/40 opacity-50 cursor-not-allowed"
            }`}
            title={b.label}
          >
            <span className="text-2xl">{b.claimed ? "✅" : b.icon}</span>
            <span className="text-[9px] font-bold text-neutral-300 truncate max-w-full px-1">
              {b.reward_pt} Phoenix Tokens{b.extra_spins ? ` · 🎰${b.extra_spins}` : ""}
            </span>
            <span className="text-[8px] text-neutral-500">{b.threshold.toLocaleString()}</span>
          </button>
        ))}
      </div>

      {/* Score strip */}
      <div className="flex justify-center gap-6 mb-4 text-sm">
        <div>
          <span className="text-neutral-500 text-[10px] uppercase tracking-widest block">Event Total</span>
          <span className="font-black text-red-300 text-xl" data-testid="fireworks-event-score">
            {(state.score || 0).toLocaleString()}
          </span>
        </div>
        {running && (
          <>
            <div>
              <span className="text-neutral-500 text-[10px] uppercase tracking-widest block">Run</span>
              <span className="font-black text-white text-xl" data-testid="fireworks-run-score">{score}</span>
            </div>
            <div>
              <span className="text-neutral-500 text-[10px] uppercase tracking-widest block">Time</span>
              <span className={`font-black text-xl ${timeLeft <= 10 ? "text-red-400" : "text-blue-300"}`}>
                {timeLeft}s
              </span>
            </div>
          </>
        )}
      </div>

      {/* Board */}
      {board && (running || resolving) ? (
        <div className="inline-grid gap-1 p-2 bg-black/70 border-2 border-red-900/50 rounded-md"
             style={{ gridTemplateColumns: `repeat(${SIZE}, minmax(0,1fr))` }}
             data-testid="fireworks-board">
          {board.map((g, i) => {
            const r = Math.floor(i / SIZE), c = i % SIZE;
            const isSel = selected === i;
            const isClr = clearing.has(i);
            return (
              <button
                key={i}
                data-testid={`fireworks-cell-${r}-${c}`}
                onClick={() => onCellClick(r, c)}
                className={`w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center text-xl sm:text-2xl border transition-all duration-150 ${
                  isClr ? "scale-0 opacity-0 border-transparent"
                  : isSel ? "border-red-400 bg-red-500/25 scale-110 shadow-[0_0_12px_rgba(220,38,38,0.6)]"
                  : "border-zinc-800/60 bg-zinc-900/40 hover:bg-zinc-800/60"
                }`}
              >
                {g}
              </button>
            );
          })}
        </div>
      ) : (
        <button
          data-testid="fireworks-start"
          onClick={startRun}
          disabled={submitting || runsToday >= maxRuns}
          className="px-8 py-4 bg-gradient-to-r from-red-600 via-white via-50% to-blue-600 text-black font-black uppercase tracking-widest text-sm hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed rounded-md shadow-[0_0_18px_rgba(59,130,246,0.4)]"
        >
          {submitting ? "Banking run…"
            : runsToday >= maxRuns ? "Daily cap reached — back tomorrow"
            : "🎇 Start 60s Fireworks Run"}
        </button>
      )}

      {user?.role === "owner" && state.active && (
        <div className="mt-6">
          <button data-testid="fireworks-owner-end" onClick={() => ownerToggle(false)}
                  className="px-3 py-1.5 border border-zinc-700 text-neutral-500 text-[10px] uppercase tracking-widest hover:text-neutral-300">
            👑 Owner: End Fireworks Event
          </button>
        </div>
      )}
    </div>
  );
}
